import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_CONFIG, getPageSEO, getFullUrl, getImageUrl } from "@/config/seo.config";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  children?: React.ReactNode;
}

/**
 * Componente SEO per gestire meta tags dinamici
 * Gli schema JSON-LD sono pre-renderizzati dallo script di build (prerender-seo.ts)
 * Usa automaticamente i dati dalla configurazione se non specificati
 */
const SEO: React.FC<SEOProps> = ({
  title: propTitle,
  description: propDescription,
  keywords: propKeywords,
  image: propImage,
  type: propType,
  noindex: propNoindex,
  children,
}) => {
  const location = useLocation();
  const path = location.pathname;

  // Ottieni configurazione SEO per questa pagina
  const pageSEO = getPageSEO(path);

  // Usa props se fornite, altrimenti fallback alla configurazione
  const title = propTitle || pageSEO?.title || SITE_CONFIG.defaultTitle;
  const description = propDescription || pageSEO?.description || SITE_CONFIG.defaultDescription;
  const keywords = propKeywords || pageSEO?.keywords || [];
  const image = getImageUrl(propImage || pageSEO?.image);
  const type = propType || pageSEO?.type || "website";
  const noindex = propNoindex ?? pageSEO?.noindex ?? false;

  const url = getFullUrl(path);

  return (
    <Helmet>
      {/* Titolo */}
      <title>{title}</title>

      {/* Meta tags base */}
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={url} />

      {/* Robots */}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}

      {/* Open Graph (Facebook, LinkedIn) */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_CONFIG.siteName} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {SITE_CONFIG.twitterHandle && <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />}

      {/* Altre meta */}
      <meta name="author" content={SITE_CONFIG.siteName} />
      <meta name="geo.region" content="IT-AQ" />
      <meta name="geo.placename" content="L'Aquila" />

      {/* Schema JSON-LD sono pre-renderizzati dallo script di build */}

      {children}
    </Helmet>
  );
};

export default SEO;
